import { CLERK_WEB_JS_VERSION } from "@/lib/clerk-web-version";

/**
 * Browser-safe helpers for warming Clerk up from the Clerk-free surfaces.
 *
 * #3408 took clerk-js off marketing pages, so a visitor who taps a sign-up
 * CTA now pays the whole cold Clerk load (script, then FAPI environment and
 * client) on /signup, staring at an empty page. These helpers let a marketing
 * page start that load as soon as the visitor shows intent, without importing
 * any Clerk client code (the import-graph guard forbids it there).
 */

/** Same-origin paths whose page mounts Clerk on arrival. */
const CLERK_ENTRY_PATH = /^\/(?:signup|login|start-project)(?:[/?#]|$)/;

/**
 * The Clerk Frontend API host encoded in a publishable key
 * (`pk_live_<base64("clerk.example.com$")>`), or null when the key is
 * missing or malformed.
 */
export function clerkFrontendApiHost(
  publishableKey: string | undefined,
): string | null {
  if (!publishableKey) return null;
  const parts = publishableKey.split("_");
  if (parts.length < 3 || parts[0] !== "pk") return null;
  const encoded = parts.slice(2).join("_");
  try {
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const decoded = atob(padded).replace(/\$$/, "");
    // A host only: no scheme, path, port or whitespace.
    return /^[a-z0-9.-]+$/i.test(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

/**
 * The exact clerk-js URL ClerkProvider requests: same host, pinned version
 * and path, so a warmed copy is the one the provider later executes.
 */
export function clerkJsScriptUrl(frontendApiHost: string): string {
  return `https://${frontendApiHost}/npm/@clerk/clerk-js@${CLERK_WEB_JS_VERSION}/dist/clerk.browser.js`;
}

/** True for a same-origin link into a page that mounts Clerk. */
export function isClerkEntryHref(href: string, origin: string): boolean {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return false;
  }
  return url.origin === origin && CLERK_ENTRY_PATH.test(url.pathname);
}
